/* Workspace — Global Cmd+K command palette
 *
 *  Open:
 *    Cmd+K / Ctrl+K     — toggle
 *    window.openCommandPalette() / 'cmdk:open' event — header 搜尋框（variant-a-v2 WorkspaceHeader）
 *  Keyboard:
 *    Esc                — close
 *    ↑ ↓                — move selection
 *    Enter              — activate
 *
 *  Sources (groups, top → bottom):
 *    1. Pages          — buildPageIndex: ROUTES + EXTRA_ROUTES（canAccessRoute）
 *                        併入類別選擇頁卡片的分區名／描述（window.CategoryHub），
 *                        另含無 route key 的卡片（台股掃描器 / GitHub / 軟體發佈中心，
 *                        可見性沿用 CategoryHub.itemVisible）。空白查詢時列出
 *                        「最近使用」＋全部可用功能，方便瀏覽不常用的頁面。
 *    2. Employees      — GET /api/employees/search?q= (debounced; admin / hr only)
 *    3. Stocks         — window.StockStudioSearch（個股工作室的 security_attrs 全表快取，
 *                        首次值得查的查詢才抓；canSearchStocks 閘門）→ #/stock-studio/<代碼>。
 *                        查詢全為數字時排在員工前（緊接頁面），否則排在員工後（cmdkGroupOrder）。
 *    4. Courses        — GET /api/courses (cached, client filter)
 *    5. Files          — GET /api/files/search?q= (debounced)
 *
 *  Mounted once globally by ShellLayout via <CommandPalette user={user} />.
 */

const CMDK_CACHE = { courses: null };
const CMDK_RECENT_KEY = 'cmdk.recent';
const CMDK_RECENT_MAX = 5;

/* ─────────────  頁面索引與搜尋（純函數；tests/test_cmdk_search.cjs 直接抽出測試）  ───────────── */
/* routes: ROUTES + EXTRA_ROUTES；hub: window.CategoryHub（可為 null）；canRoute: canAccessRoute。
 * 有 route key 的項目可見性只看 canRoute（與舊版 Cmd+K 相同）；hub 卡片的 open helper
 * 名（openName）沿用為開啟方式，與類別頁卡片同一個主要入口（外開新分頁）。 */
function buildPageIndex(user, routes, hub, canRoute) {
  const index = [];
  const seen = new Set();
  routes.forEach((r) => {
    if (seen.has(r.key) || !canRoute(user, r.key)) return;
    seen.add(r.key);
    const found = hub ? hub.findItem(r.key) : null;
    index.push({
      ...r, kind: 'page',
      cat: found ? found.cat.name : '',
      desc: found ? (found.item.d || '') : '',
      kw: found ? (found.item.kw || []) : [],
      openName: found ? found.item.open : undefined,
      href: found && found.item.ext ? found.item.href : undefined,
    });
  });
  if (hub) {
    hub.CATEGORIES.forEach((cat) => {
      const items = cat.items || (cat.sections || []).reduce((acc, s) => acc.concat(s.items), []);
      items.forEach((it) => {
        if (it.key || !hub.itemVisible(user, it, cat)) return;
        index.push({
          kind: 'page', key: it.previewKey, label: it.n, icon: 'arrow-up-right',
          section: cat.name, cat: cat.name, desc: it.d || '', kw: it.kw || [],
          openName: it.open, href: it.href,
        });
      });
    });
  }
  return index;
}

/* 空白分隔多關鍵字、每個都要命中（AND）；名稱開頭 > 名稱 > 同義詞 kw > key > 分區 > 描述。
 * 同分維持索引原順序。kw（hub 卡片的 ⌘K 同義詞，如「請假」→ 假勤申請）：關鍵字包含輸入
 * 片段即命中；輸入整句包含某個 ≥2 字的同義詞也算（「我要請假」）。kw 只參與比對，
 * index 由 buildPageIndex 依權限建好，故不會讓無權頁面出現。 */
function searchPages(index, query) {
  const tokens = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return index.slice();
  const scored = [];
  index.forEach((p, i) => {
    const label = (p.label || '').toLowerCase();
    const key = (p.key || '').toLowerCase();
    const meta = `${p.section || ''} ${p.cat || ''}`.toLowerCase();
    const desc = (p.desc || '').toLowerCase();
    const kw = Array.from(p.kw || [], (k) => String(k).toLowerCase());
    let score = 0;
    for (const t of tokens) {
      const s = label.startsWith(t) ? 100
        : label.includes(t) ? 60
        : kw.some((k) => k.includes(t) || (k.length >= 2 && t.includes(k))) ? 50
        : key.includes(t) ? 40
        : meta.includes(t) ? 20
        : desc.includes(t) ? 10 : 0;
      if (!s) return;
      score += s;
    }
    scored.push({ p, score, i });
  });
  scored.sort((a, b) => b.score - a.score || a.i - b.i);
  return scored.map((x) => x.p);
}

/* 員工搜尋來源的角色閘門 —— 對齊後端 GET /api/employees/search 的
 * require_role("hr", "admin")（routes_employees.py；hr 角色在 auth_rbac 的
 * _HR_ALLOWED_PREFIXES 白名單內保留）。受限帳號（analyst / analyst_lite / non_staff，
 * SPEC §9 #14）一律不打員工 API；helper 不存在時 fail-closed。 */
function canSearchEmployees(user) {
  if (typeof isRestrictedAccount !== 'function' || isRestrictedAccount(user)) return false;
  if (typeof isAdmin === 'function' && isAdmin(user)) return true;
  return typeof hasRole === 'function' && hasRole(user, 'hr');
}

/* 搜尋框 placeholder：只有能搜員工的人才提示「員工」、能搜股票的人才提示「股票」。 */
function cmdkPlaceholder(canEmployees, canStocks) {
  return `搜尋功能、${canEmployees ? '員工、' : ''}${canStocks ? '股票、' : ''}檔案、課程…`
    + `（可用功能描述的關鍵字，如「下注」「發票」${canStocks ? '，或股票代碼／名稱如「2330」' : ''}）`;
}

/* ── 股票來源 —— 共用個股工作室（hr-stock-studio.jsx）的 window.StockStudioSearch：
 * 同一份 security_attrs 全表快取（只抓一次）與同一套排序（代碼完全相符 > 代碼前綴 >
 * 上市中 > 代碼字典序），面板只取前 CMDK_STOCK_MAX 筆。 */
const CMDK_STOCK_MAX = 5;

/* 角色閘門：能進個股工作室，且後端會放行 /api/quantdata —— NonEmployeeGuardMiddleware
 * 對 analyst_lite-only 帳號 403（main.py _ANALYST_LITE_ALLOW_PREFIXES 不含 /api/quantdata）；
 * analyst_lite+non_staff 因白名單聯集仍可用、兼任員工角色者走 privileged 放行。
 * 故受限帳號須其路由白名單含 'quantdata'（同 _ANALYST_LITE_ROUTES 的鏡射）。
 * helper 不存在時 fail-closed。 */
function canSearchStocks(user) {
  if (!user || typeof canAccessRoute !== 'function' || typeof restrictedRouteKeys !== 'function') return false;
  if (!canAccessRoute(user, 'stock-studio')) return false;
  const restricted = restrictedRouteKeys(user);
  return !restricted || restricted.includes('quantdata');
}

/* 查詢正規化：去頭尾空白、英文轉大寫（ETF 代碼如 00631L；qdSearch 大小寫敏感）。 */
function normalizeStockQuery(q) { return String(q || '').trim().toUpperCase(); }

/* 是否值得查股票（才觸發首次抓全表）：含數字、英文字母或 CJK 字；空白／純符號不查。 */
function isStockQuery(q) {
  return /[0-9A-Z\u3400-\u9fff\uf900-\ufaff]/.test(normalizeStockQuery(q));
}

/* API 群組順序（接在「頁面」之後）：查詢全為數字（像股票代碼）→ 股票緊接頁面、排在員工前；
 * 其餘 → 員工之後。 */
function cmdkGroupOrder(q) {
  return /^\d+$/.test(normalizeStockQuery(q))
    ? ['stock', 'employee', 'course', 'file']
    : ['employee', 'stock', 'course', 'file'];
}

/* StockStudioSearch.search() 結果 → 面板項目（前 CMDK_STOCK_MAX 筆）。 */
function toStockItems(results) {
  return Array.from(results || []).slice(0, CMDK_STOCK_MAX).map((r) => ({
    kind: 'stock', key: r.symbol, symbol: r.symbol, name: r.name || '',
    industry: r.industry || '', delisted: r.listing_status === 'delisted',
  }));
}
/* ─────────────  /頁面索引與搜尋  ───────────── */

function readRecent() {
  try {
    const v = JSON.parse(localStorage.getItem(CMDK_RECENT_KEY) || '[]');
    return Array.isArray(v) ? v : [];
  } catch (_) { return []; }
}
function rememberRecent(key) {
  if (!key) return;
  try {
    const next = [key, ...readRecent().filter((k) => k !== key)].slice(0, CMDK_RECENT_MAX);
    localStorage.setItem(CMDK_RECENT_KEY, JSON.stringify(next));
  } catch (_) { /* storage blocked — recent list is a convenience only */ }
}

function CommandPalette({ user }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [selected, setSelected] = React.useState(0);
  const [empHits, setEmpHits] = React.useState([]);
  const [fileHits, setFileHits] = React.useState([]);
  const [courseHits, setCourseHits] = React.useState([]);
  const [stockHits, setStockHits] = React.useState([]);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);

  /* === Global Cmd+K / Ctrl+K listener === */
  React.useEffect(() => {
    const onKey = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(o => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('cmdk:open', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('cmdk:open', onOpen);
    };
  }, [open]);

  /* === Focus + reset on open === */
  React.useEffect(() => {
    if (open) {
      setQuery(''); setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 50);
      // Lazy-load courses once
      if (!CMDK_CACHE.courses) {
        api('GET', '/courses').then(d => {
          CMDK_CACHE.courses = Array.isArray(d) ? d : [];
        }).catch(() => { CMDK_CACHE.courses = []; });
      }
    }
  }, [open]);

  /* === Debounced employee + file search === */
  React.useEffect(() => {
    if (!open || !query.trim()) {
      setEmpHits([]); setFileHits([]); setCourseHits([]); setStockHits([]);
      return;
    }
    const q = query.trim();
    let alive = true;
    const t = setTimeout(() => {
      // Employees (hr/admin-only endpoint) — gate mirrors the backend role check
      if (canSearchEmployees(user)) {
        api('GET', `/employees/search?q=${encodeURIComponent(q)}&limit=5`)
          .then(d => { if (alive) setEmpHits(Array.isArray(d) ? d : []); })
          .catch(() => {});
      }
      // Files
      api('GET', `/files/search?q=${encodeURIComponent(q)}`)
        .then(d => { if (alive) setFileHits((d?.results || []).slice(0, 5)); })
        .catch(() => {});
      // Courses (client filter on cached list)
      const cs = CMDK_CACHE.courses || [];
      setCourseHits(
        cs.filter(c =>
          (c.title || '').toLowerCase().includes(q.toLowerCase()) ||
          (c.description || '').toLowerCase().includes(q.toLowerCase())
        ).slice(0, 5)
      );
      // Stocks — lazy: the stock studio's security_attrs list is fetched on the first
      // stock-worthy query (never on open) and cached there. Any error / 403 → no group.
      const ss = window.StockStudioSearch;
      if (!CMDK_CACHE.stocksOff && ss && canSearchStocks(user) && isStockQuery(q)) {
        ss.search(normalizeStockQuery(q))
          .then(d => { if (alive) setStockHits(toStockItems(d && d.results)); })
          .catch((e) => {
            if (e && e.status === 403) CMDK_CACHE.stocksOff = true;   // no access: stop retrying this session
            if (alive) setStockHits([]);
          });
      } else {
        setStockHits([]);
      }
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [query, open, user]);

  /* === Build unified result list (in render order) === */
  const pageIndex = React.useMemo(() => {
    if (!open) return [];
    const routes = [...ROUTES, ...(window.EXTRA_ROUTES || [])];
    return buildPageIndex(user, routes, window.CategoryHub || null, canAccessRoute);
  }, [open, user]);

  // 空白查詢：recent = 最近使用、pages = 其餘全部功能；有查詢：pages = 排序後前 12 筆
  const { recent, pages } = React.useMemo(() => {
    if (query.trim()) return { recent: [], pages: searchPages(pageIndex, query).slice(0, 12) };
    const byKey = new Map(pageIndex.map((p) => [p.key, p]));
    const rec = readRecent().map((k) => byKey.get(k)).filter(Boolean);
    return { recent: rec, pages: pageIndex.filter((p) => !rec.includes(p)) };
  }, [query, pageIndex]);

  const items = React.useMemo(() => {
    const list = [];
    if (recent.length > 0) {
      list.push({ kind: 'header', label: '最近使用' });
      recent.forEach(p => list.push(p));
    }
    if (pages.length > 0) {
      list.push({ kind: 'header', label: query ? '頁面' : '全部功能' });
      pages.forEach(p => list.push(p));
    }
    const groups = {
      employee: () => {
        if (empHits.length === 0) return;
        list.push({ kind: 'header', label: '員工' });
        empHits.forEach(e => list.push({ kind: 'employee', ...e }));
      },
      stock: () => {
        if (stockHits.length === 0) return;
        list.push({ kind: 'header', label: '股票' });
        stockHits.forEach(s => list.push(s));
      },
      course: () => {
        if (courseHits.length === 0) return;
        list.push({ kind: 'header', label: '課程' });
        courseHits.forEach(c => list.push({ kind: 'course', ...c }));
      },
      file: () => {
        if (fileHits.length === 0) return;
        list.push({ kind: 'header', label: '檔案' });
        fileHits.forEach(f => list.push({ kind: 'file', ...f }));
      },
    };
    cmdkGroupOrder(query).forEach((g) => groups[g]());
    return list;
  }, [recent, pages, empHits, stockHits, courseHits, fileHits, query]);

  // Selectable items (skip headers)
  const selectable = items.filter(i => i.kind !== 'header');
  const selectedItem = selectable[selected];

  /* === Reset selection when items change === */
  React.useEffect(() => { setSelected(0); }, [items.length]);

  /* === Scroll selected into view === */
  React.useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-cmdk-idx="${selected}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  const activate = (item) => {
    if (!item) return;
    // The employees page is admin-only (ROUTES roles: []): hr may search employees but would
    // land on Forbidden, so for them a hit is an in-palette lookup (code · department) only.
    if (item.kind === 'employee'
        && !(typeof canAccessRoute === 'function' && canAccessRoute(user, 'employees'))) return;
    setOpen(false);
    switch (item.kind) {
      case 'page': {
        rememberRecent(item.key);
        if (item.open) { item.open(); return; }   // 無 SPA 頁的工具（ytkb）→ 自訂開啟（直開新分頁）
        // 類別頁卡片的外開 helper（同步 window.open 過 popup blocker → 換 cookie → 導向）
        const fn = item.openName && window[item.openName];
        if (typeof fn === 'function') { fn(); return; }
        if (item.href) { window.open(item.href, '_blank', 'noopener'); return; }   // 純外連（GitHub）
        if (item.external) { window.location.href = item.external; return; }
        navigate('/' + item.key);
        break;
      }
      case 'employee': navigate(`/employees?q=${encodeURIComponent(item.name)}`); break;
      // recents (cmdk.recent) hold page keys only → stock picks are not recorded
      case 'stock':    navigate(`/stock-studio/${encodeURIComponent(item.symbol)}`); break;
      case 'course':   navigate('/courses'); break;
      case 'file':     navigate('/files'); break;
    }
  };

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected(s => Math.min(selectable.length - 1, s + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected(s => Math.max(0, s - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      activate(selectedItem);
    }
  };

  if (!open) return null;

  // Expressive look (mockup .pal / .pal-top / .po / .pal-foot): every value goes through
  // uiPick/uiStyle so the Bento palette renders exactly as before. Search, keyboard and focus
  // logic are untouched. Full-screen on phones via gx-css-p-cmdk (.gx-cmdk-panel).
  const gxC = GxT.color;
  return (
    <div
      onClick={() => setOpen(false)}
      style={uiStyle({
        position: 'fixed', inset: 0, background: 'rgba(14,20,17,0.42)',
        zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '12vh 20px 20px',
      }, { background: 'rgba(0,0,0,.42)', padding: '9vh 24px 24px' })}
    >
      <div
        className={GxCore.cls(undefined, 'gx-cmdk-panel')}
        onClick={(e) => e.stopPropagation()}
        style={uiStyle({
          width: 580, maxWidth: '100%', background: HR.surface,
          borderRadius: 8, boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          maxHeight: '70vh',
        }, {
          width: 680, background: gxC.s1, borderRadius: GxT.radius.card,
          boxShadow: '0 24px 60px rgba(0,0,0,.3)', maxHeight: '78vh', fontFamily: GxT.font,
        })}
      >
        {/* Input */}
        <div className={GxCore.cls(undefined, 'gx-cmdk-top')} style={uiStyle({
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 18px', borderBottom: `1px solid ${HR.rule}`,
        }, {
          margin: '12px 12px 4px', padding: '0 12px 0 16px', minHeight: 52,
          // outlined search field (= inputStyle under GX); borderBottom repeats the border so the
          // Bento key it overrides can never win by property order. Focus ring: gx-css-p-cmdk.
          border: `1px solid ${gxC.ol}`, borderBottom: `1px solid ${gxC.ol}`,
          borderRadius: GxT.radius.input, background: gxC.card, color: gxC.onV,
        })}>
          {/* Expressive phone view is full-screen (no backdrop to tap, no Esc key) → explicit
              close button; hidden ≥900px by gx-css-p-cmdk. Bento never renders it. */}
          {gxUiOn() && (
            <button type="button" className="gx-cmdk-close" onClick={() => setOpen(false)} aria-label="關閉搜尋">
              <Icon name="x" size={18} />
            </button>
          )}
          <Icon name="search" size={uiPick(16, 18)} />
          <input
            ref={inputRef}
            autoFocus
            value={query}
            placeholder={cmdkPlaceholder(canSearchEmployees(user), canSearchStocks(user))}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKey}
            style={uiStyle({
              flex: 1, border: 'none', outline: 'none',
              fontFamily: HR.sans, fontSize: FontSizes.base, color: HR.ink,
              background: 'transparent',
            }, { minWidth: 0, height: 48, fontSize: 17, fontWeight: 500 })}
          />
          <kbd style={kbdStyle()}>Esc</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} style={uiStyle({
          flex: 1, overflowY: 'auto', padding: '6px 0',
        }, { padding: '4px 12px 12px' })}>
          {items.length === 0 ? (
            <div style={uiStyle({
              padding: '40px 20px', textAlign: 'center',
              color: HR.inkFaint, fontSize: FontSizes.xs,
            }, { color: gxC.onV, fontSize: 14 })}>
              {query ? '無符合結果' : '開始輸入以搜尋…'}
            </div>
          ) : (
            items.map((it, i) => {
              if (it.kind === 'header') {
                return (
                  <div key={`h-${i}`} style={uiStyle({
                    padding: '10px 18px 6px', fontSize: FontSizes.trading_sm,
                    letterSpacing: '0.14em', textTransform: 'uppercase',
                    color: HR.inkFaint, fontWeight: 600,
                  }, {
                    padding: '12px 8px 6px', fontSize: 12, letterSpacing: 0,
                    textTransform: 'none', color: gxC.onV, fontWeight: 800,
                  })}>{it.label}</div>
                );
              }
              // Compute index among selectable
              const selIdx = selectable.indexOf(it);
              const isSelected = selIdx === selected;
              return (
                <div
                  key={`${it.kind}-${it.key || it.id}`}
                  data-cmdk-idx={selIdx}
                  onClick={() => activate(it)}
                  onMouseEnter={() => setSelected(selIdx)}
                  style={uiStyle({
                    display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: 12,
                    padding: '9px 18px', cursor: 'pointer', alignItems: 'center',
                    background: isSelected ? HR.accentSoft : 'transparent',
                    borderLeft: isSelected ? `3px solid ${HR.accent}` : '3px solid transparent',
                  }, {
                    padding: '10px 12px', minHeight: 48, borderRadius: GxT.radius.btnSm,
                    background: isSelected ? gxC.sc : 'transparent',
                    color: isSelected ? gxC.onSc : gxC.on, borderLeft: 'none',
                  })}
                >
                  <Icon name={iconFor(it)} size={uiPick(14, 18)} />
                  <div style={{ minWidth: 0 }}>
                    <div style={uiStyle({
                      fontSize: FontSizes.trading_md,
                      color: isSelected ? HR.accent : HR.ink,
                      fontWeight: 500,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }, { fontSize: 15, color: 'inherit', fontWeight: isSelected ? 800 : 600 })}>{primaryText(it)}</div>
                    {secondaryText(it) && (
                      <div style={uiStyle({
                        fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 2,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }, { fontSize: 12, color: isSelected ? gxC.onSc : gxC.onV })}>{secondaryText(it)}</div>
                    )}
                  </div>
                  <span style={uiStyle({ fontSize: FontSizes.trading_md, color: HR.inkFaint, fontFamily: HR.mono, letterSpacing: '0.06em' },
                    { fontSize: 12, fontWeight: 700, color: isSelected ? gxC.onSc : gxC.onV, fontFamily: GxT.font, letterSpacing: 0 })}>
                    {kindBadge(it)}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={uiStyle({
          padding: '8px 18px', borderTop: `1px solid ${HR.rule}`,
          background: HR.surfaceAlt,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontFamily: HR.mono, letterSpacing: '0.04em',
        }, {
          padding: '12px 20px', borderTop: `1px solid ${gxC.olv}`, background: 'transparent',
          gap: 12, flexWrap: 'wrap', fontSize: 12, color: gxC.onV, fontFamily: GxT.font, letterSpacing: 0,
        })}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <span><kbd style={kbdStyle()}>↑</kbd><kbd style={kbdStyle()}>↓</kbd> 移動</span>
            <span><kbd style={kbdStyle()}>↵</kbd> 開啟</span>
          </span>
          <span>{selectable.length} 個結果</span>
        </div>
      </div>
    </div>
  );
}

function openCommandPalette() {
  window.dispatchEvent(new Event('cmdk:open'));
}

/* Header 搜尋框（WorkspaceHeader 掛載）：外觀是輸入框、點了開 Cmd+K 面板。
 * 取代舊的右下角浮動提示（關掉後就再也找不到入口）。窄螢幕只留放大鏡
 * （.va2-search-label / .va2-search-kbd 由 index.html media query 隱藏）。 */
function CommandSearchButton() {
  const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform || '');
  return (
    <button
      type="button"
      className="va2-search-btn"
      onClick={openCommandPalette}
      title={`搜尋功能（${isMac ? '⌘' : 'Ctrl'}+K）`}
      aria-label="搜尋功能"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        minWidth: 0, padding: '6px 10px', borderRadius: 6,
        border: `1px solid ${VA2.rule}`, background: VA2.surface || '#fff',
        color: VA2.inkFaint, cursor: 'pointer', whiteSpace: 'nowrap',
        fontFamily: 'inherit', fontSize: FontSizes.trading_xl,
      }}
    >
      <Icon name="search" size={14} />
      <span className="va2-search-label">搜尋功能…</span>
      <span className="va2-search-kbd" style={{ marginLeft: 12 }}>
        <kbd style={kbdStyle()}>{isMac ? '⌘' : 'Ctrl'}</kbd><kbd style={kbdStyle()}>K</kbd>
      </span>
    </button>
  );
}

function kbdStyle(opts = {}) {
  const dark = opts.dark;
  return uiStyle({
    display: 'inline-block', padding: '1px 6px', borderRadius: 3,
    background: dark ? 'rgba(255,255,255,0.15)' : HR.surfaceAlt,
    border: dark ? '1px solid rgba(255,255,255,0.2)' : `1px solid ${HR.rule}`,
    fontFamily: HR.mono, fontSize: FontSizes.trading_md,
    color: dark ? '#fff' : HR.inkSoft,
    margin: '0 2px',
  }, dark ? { borderRadius: GxT.radius.chipSm } : {
    padding: '2px 7px', borderRadius: GxT.radius.chipSm, background: GxT.color.card,
    border: `1px solid ${GxT.color.olv}`, fontFamily: GxT.font, fontSize: 11, fontWeight: 700,
    color: GxT.color.onV,
  });
}

/* Expressive ⌘K: phone = full-screen search view (mockup .pal < 900px); focus ring on the
 * outlined search field (same values as the shared input rules in gx-css-pages; the input
 * itself keeps Bento's outline:none). No-op under Bento. */
GxCore.injectCss('gx-css-p-cmdk', String.raw`
html[data-ui-shell="expressive"] .gx-cmdk-top:focus-within{border-color:var(--gx-p)!important;background-color:var(--gx-card)!important;outline:2px solid var(--gx-p);outline-offset:-2px}
html[data-ui-shell="expressive"] .gx-cmdk-top input::placeholder{color:var(--gx-faint);opacity:1}
html[data-ui-shell="expressive"] .gx-cmdk-panel{animation:gx-cmdk-in .4s var(--gx-spring)}
@keyframes gx-cmdk-in{from{opacity:0;transform:translateY(16px) scale(.98)}}
html[data-ui-shell="expressive"] .gx-cmdk-close{display:none;flex:none;width:40px;height:40px;margin-left:-8px;place-items:center;border:0;border-radius:12px;background:transparent;color:var(--gx-on);cursor:pointer}
html[data-ui-shell="expressive"] .gx-cmdk-close:hover{background:var(--gx-s3)}
@media (max-width:899px){
html[data-ui-shell="expressive"] .gx-cmdk-panel{position:fixed;inset:0;width:auto!important;max-width:none!important;max-height:none!important;border-radius:0!important;box-shadow:none!important;padding-bottom:env(safe-area-inset-bottom)}
html[data-ui-shell="expressive"] .gx-cmdk-close{display:grid}
html[data-ui-shell="expressive"] .gx-cmdk-top kbd{display:none!important}
}
@media (prefers-reduced-motion:reduce){
html[data-ui-shell="expressive"] .gx-cmdk-panel{animation:none}
}
`);

function iconFor(it) {
  if (it.kind === 'page') return it.icon || 'arrow-right';
  if (it.kind === 'employee') return 'file-text';
  if (it.kind === 'stock') return 'trending-up';
  if (it.kind === 'course') return 'book';
  if (it.kind === 'file') return 'folder';
  return 'more';
}
function primaryText(it) {
  if (it.kind === 'page') return it.label;
  if (it.kind === 'employee') return it.name;
  if (it.kind === 'stock') return [it.symbol, it.name].filter(Boolean).join(' ');
  if (it.kind === 'course') return it.title;
  if (it.kind === 'file') return it.name;
  return '';
}
function secondaryText(it) {
  if (it.kind === 'page') return [it.section, it.desc].filter(Boolean).join(' · ');
  if (it.kind === 'employee') return [it.emp_code, it.department_name].filter(Boolean).join(' · ');
  if (it.kind === 'stock') return [it.industry, it.delisted ? '已下市' : ''].filter(Boolean).join(' · ');
  if (it.kind === 'course') return it.description || `${it.file_count || 0} 個檔案`;
  if (it.kind === 'file') return it.path;
  return '';
}
function kindBadge(it) {
  const m = { page: 'PAGE', employee: 'EMP', stock: 'STOCK', course: 'COURSE', file: 'FILE' };
  return m[it.kind] || '';
}

window.CommandPalette = CommandPalette;
window.CommandSearchButton = CommandSearchButton;
window.openCommandPalette = openCommandPalette;
// Expressive 頂列搜尋膠囊（expressive-shell.jsx → GxModel.searchPillText）用同一個閘門決定是否提示「股票」
window.cmdkCanSearchStocks = canSearchStocks;
